import {
  createContext,
  useRef,
  useContext,
  useCallback,
  useMemo,
  useState,
  ReactNode,
} from "react";

import { getErrorDetail } from "../components/utility/utils";
import { useOrchidWebSocket } from "contexts/WebSocketContext";
import { mergeDataByKey } from "utils/fn";
import { useUser } from "./UserContext";
import { usePrivateAPIData } from "contexts/api-data-provider";

interface GroupSessionsContextProps {
  groupSessionsError: string | null;
  getUserGroupSessions: () => Array<GroupSessionMetaData> | undefined;
  getUserGroupSessionSeriesList: () =>
    | Array<GroupSessionSeriesMetaData>
    | undefined;
  reloadGroupSessions: () => void;
}

export const GroupSessionsContext = createContext<GroupSessionsContextProps>(
  {} as GroupSessionsContextProps,
);

function getUsedGroupSessionSeriesIds(
  groupSessions: Array<GroupSessionMetaData>,
): Set<number> {
  return new Set(
    groupSessions.map((groupSession) => groupSession.group_sessions_series_id),
  );
}

function groupsessionSeriesFromApiData(data: any): GroupSessionSeriesMetaData {
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    cancellation_notice_hours: data.cancellation_notice_hours,
    cancellation_policy_flat_fee: data.cancellation_policy_flat_fee,
    cancellation_policy_percent_fee: data.cancellation_policy_percent_fee,
    groupSessions: [],
  };
}

function fillSessionInSeries(
  group_session_series: Array<GroupSessionSeriesMetaData>,
  sessions: Array<GroupSessionMetaData>,
): Array<GroupSessionSeriesMetaData> {
  return group_session_series.map((series) => {
    return {
      ...series,
      groupSessions: sessions.filter(
        (session) => series.id === session.group_sessions_series_id,
      ),
    };
  });
}

function useGroupSessionsSeriesBasic(reloadTimestamp: number | undefined) {
  const {
    groupSessionsSeries: { result: groupSessionsSeriesAPIData, errorText },
  } = usePrivateAPIData(
    {
      groupSessionsSeries: {
        resource:
          "/api/scheduler/v1/group_timeslot_public/group_session_series",
      },
    },
    reloadTimestamp,
  );

  return useMemo(() => {
    return {
      groupSessionSeriesBasicError: errorText
        ? `Failed to get group sessions: ${getErrorDetail(errorText)}`
        : null,
      groupSessionsSeriesBasic: groupSessionsSeriesAPIData
        ? groupSessionsSeriesAPIData.map(groupsessionSeriesFromApiData)
        : undefined,
    };
  }, [errorText, groupSessionsSeriesAPIData]);
}

function useGroupSessionsSnapshot(reloadTimestamp: number | undefined) {
  const {
    groupSessions: { result: groupSessionsSnapshot, errorText },
  } = usePrivateAPIData(
    {
      groupSessions: { resource: "/api/scheduler/v1/snapshot/group_sessions" },
    },
    reloadTimestamp,
  );

  return useMemo(() => {
    return {
      groupSessionsSnapshotError: errorText
        ? `Failed to get group sessions: ${getErrorDetail(errorText)}`
        : null,
      groupSessionsSnapshot,
    };
  }, [errorText, groupSessionsSnapshot]);
}

interface Props {
  children: ReactNode;
}

// Usage:
// getUserGroupSessions - lists all the group sessions, gives undefined when group sessions are not loaded yet
// getUserGroupSessionSeriesList - lists all group series user hosts or booked, gives undefined when not loeded yet
// reloadGroupSessions - use when expecting change to any group session (cancel, book, update group session parameters)
export function GroupSessionsProvider({ children }: Props) {
  const { user } = useUser();
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [reloadTimestamp, setReloadTimestamp] = useState<number | undefined>(
    undefined,
  );
  const { groupSessionsSnapshotError, groupSessionsSnapshot } =
    useGroupSessionsSnapshot(reloadTimestamp);
  const [groupSessionsUpdates, setGroupSessionsUpdates] = useState<
    Array<GroupSessionMetaData>
  >([]);
  const { groupSessionSeriesBasicError, groupSessionsSeriesBasic } =
    useGroupSessionsSeriesBasic(reloadTimestamp);

  const groupSessions = useMemo(
    () =>
      groupSessionsSnapshot
        ? mergeDataByKey(
            groupSessionsSnapshot,
            groupSessionsUpdates,
            "group_session_id",
          )
        : undefined,
    [groupSessionsSnapshot, groupSessionsUpdates],
  );

  const userGroupSessionsSeries = useMemo(() => {
    const userSessionSeriesIds = new Set([
      ...(groupSessionsSnapshot
        ? getUsedGroupSessionSeriesIds(groupSessionsSnapshot)
        : []),
      ...getUsedGroupSessionSeriesIds(groupSessionsUpdates),
    ]);
    return groupSessionsSeriesBasic && groupSessions
      ? fillSessionInSeries(
          groupSessionsSeriesBasic.filter((series) =>
            userSessionSeriesIds.has(series.id),
          ),
          groupSessions,
        )
      : undefined;
  }, [
    groupSessionsSnapshot,
    groupSessionsUpdates,
    groupSessionsSeriesBasic,
    groupSessions,
  ]);

  const reloadGroupSessions = useCallback(() => {
    if (reloadTimer.current !== null) return;
    reloadTimer.current = setTimeout(() => {
      reloadTimer.current = null;
      setReloadTimestamp(new Date().getTime());
    }, 0);
  }, [reloadTimer]);

  const getUserGroupSessions = useCallback(
    function (): Array<GroupSessionMetaData> | undefined {
      if (!user?.email_confirmed || !user?.signup_form_finished) {
        return [];
      }

      if (
        reloadTimestamp === undefined &&
        groupSessionsSnapshot === undefined
      ) {
        reloadGroupSessions();
      }
      return groupSessions;
    },
    [
      reloadGroupSessions,
      user,
      reloadTimestamp,
      groupSessionsSnapshot,
      groupSessions,
    ],
  );

  const getUserGroupSessionSeriesList = useCallback(
    function (): Array<GroupSessionSeriesMetaData> | undefined {
      if (!user?.email_confirmed || !user?.signup_form_finished) {
        return [];
      }

      if (
        reloadTimestamp === undefined &&
        (groupSessionsSnapshot === undefined ||
          groupSessionsSeriesBasic === undefined)
      ) {
        reloadGroupSessions();
      }

      return userGroupSessionsSeries;
    },
    [
      reloadGroupSessions,
      user,
      reloadTimestamp,
      groupSessionsSnapshot,
      groupSessionsSeriesBasic,
      userGroupSessionsSeries,
    ],
  );

  const { lastMessage } = useOrchidWebSocket();

  // TODO: this could be better
  if (lastMessage && lastMessage.event_type === "group_session") {
    const changed =
      groupSessions !== undefined &&
      lastMessage.group_sessions.some(
        (groupSession) =>
          JSON.stringify(
            groupSessions.find(
              (d) => d.group_session_id === groupSession.group_session_id,
            ) || {},
          ) !== JSON.stringify(groupSession),
      );
    if (changed) {
      setGroupSessionsUpdates(
        mergeDataByKey(
          groupSessionsUpdates,
          lastMessage.group_sessions,
          "group_session_id",
        ),
      );
    }
  }

  return (
    <GroupSessionsContext.Provider
      value={{
        groupSessionsError: groupSessionsSnapshotError
          ? groupSessionsSnapshotError
          : groupSessionSeriesBasicError,
        getUserGroupSessions,
        getUserGroupSessionSeriesList,
        reloadGroupSessions,
      }}
    >
      {children}
    </GroupSessionsContext.Provider>
  );
}

export const useGroupSessionsProvider = () => {
  return useContext(GroupSessionsContext);
};
